import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Typography } from 'extended-oxygen-elements';
import { ErrorBox } from 'element';
import { activeSitesAsSelectOptionsArraySelector } from 'state/AccountTree';
import { getAccountId } from 'state/Account/selectors';
import { DutiesApi } from 'lib/Api';
import { BOX_TOAST_NOTIFIER_MESSAGE_SHOW } from 'state/ToastNotifier';
import { ConfigureDutyModal, DutiesTable, LocationTabs } from './components';
import { DayOfWeek, DAYS_OF_WEEK, Duty, DutyConfiguration, SiteDutyConfig } from './types';
import { convertToModalFormat, ensureAllDuties, groupByDay } from './utils/dataTransformers';
import { getErrorMessages } from './utils/errorUtils';
import { formatTimeForBackend } from './utils/timeUtils';
import * as PageStyles from './styles/pageStyles';


export function DutiesByLocation() {
  const sitesDropDownOptions = useSelector(activeSitesAsSelectOptionsArraySelector);
  const accountId = useSelector(getAccountId);
  const dispatch = useDispatch();

  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>('monday');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [siteConfigs, setSiteConfigs] = useState<Record<DayOfWeek, SiteDutyConfig[]> | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<SiteDutyConfig | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const fetchDutiesForLocation = useCallback(async (locationId: string) => {
    if (!locationId) {
      return;
    }

    setIsLoading(true);
    setErrors([]);

    try {
      // Fetch all duty types (reference data)
      const dutiesResponse = await DutiesApi.getDuties();

      if (!dutiesResponse?.data || !Array.isArray(dutiesResponse.data)) {
        setErrors(['Unexpected response format from duties API.']);
        return;
      }

      const duties = dutiesResponse.data as Duty[];

      // Fetch existing site duty requirements for this location
      const requirementsResponse = await DutiesApi.getRequirements(locationId);
      const existingConfigs = (requirementsResponse?.data || []) as SiteDutyConfig[];

      // Group by day
      const configsByDay = groupByDay(existingConfigs);

      // Ensure all duty types are represented for each day
      const completeConfigs = {} as Record<DayOfWeek, SiteDutyConfig[]>;
      DAYS_OF_WEEK.forEach(day => {
        completeConfigs[day.value] = ensureAllDuties(
          duties,
          configsByDay[day.value],
          day.value
        );
      });

      setSiteConfigs(completeConfigs);
    } catch (error: any) {
      setErrors(getErrorMessages(error));
      setSiteConfigs(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize with the first location
  useEffect(() => {
    if (sitesDropDownOptions.length > 0 && !selectedLocationId) {
      setSelectedLocationId(sitesDropDownOptions[0].value);
    }
  }, [sitesDropDownOptions, selectedLocationId]);

  // Fetch duties and requirements when location changes
  useEffect(() => {
    if (selectedLocationId) {
      fetchDutiesForLocation(selectedLocationId);
    }
  }, [selectedLocationId, fetchDutiesForLocation]);

  // Get configurations for the selected day (memoized)
  const dutiesForDay = useMemo(() => {
    return siteConfigs?.[selectedDay] || [];
  }, [siteConfigs, selectedDay]);

  const handleLocationChange = useCallback((event: SelectChangeEvent) => {
    setSelectedLocationId(event.target.value);
    // Data will be fetched by the useEffect that watches selectedLocationId
  }, []);

  const handleDayChange = useCallback((day: DayOfWeek) => {
    setSelectedDay(day);
  }, []);

  const handleConfigure = useCallback((dutyType: string) => {
    const config = dutiesForDay.find(c => c.duty.id === dutyType);
    if (config) {
      setSelectedConfig(config);
      setIsModalOpen(true);
    }
  }, [dutiesForDay]);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedConfig(null);
  }, []);

  const handleModalSave = useCallback(async (updatedDuty: DutyConfiguration) => {
    setIsSaving(true);
    setErrors([]);

    try {
      const formattedRequirements = updatedDuty.requirements?.map(req => ({
        startTime: formatTimeForBackend(req.startTime),
        endTime: formatTimeForBackend(req.endTime),
        minPersonnel: req.minPersonnel
      })) || [];

      // Prepare API payload
      // Note: API expects lowercase day names (e.g., "monday", "tuesday")
      const payload = {
        siteId: selectedLocationId,
        dutyId: updatedDuty.duty.id,
        day: selectedDay,
        requirements: formattedRequirements
      };

      // Make API call to save duty configuration
      await DutiesApi.saveRequirements(selectedLocationId, payload);

      // Refetch all duties to ensure data consistency
      await fetchDutiesForLocation(selectedLocationId);

      // Show success notification
      dispatch(BOX_TOAST_NOTIFIER_MESSAGE_SHOW({
        message: 'Duty configuration saved successfully',
        type: 'success'
      }));

      // Close modal
      setIsModalOpen(false);
      setSelectedConfig(null);

    } catch (error: any) {
      setErrors(getErrorMessages(error));
    } finally {
      setIsSaving(false);
    }
  }, [selectedDay, selectedLocationId, accountId, dispatch, fetchDutiesForLocation]);

  // Convert selected config to modal format
  const modalDuty = useMemo(() => {
    return selectedConfig ? convertToModalFormat(selectedConfig) : null;
  }, [selectedConfig]);

  return (
    <Box sx={PageStyles.pageContainerStyles}>
      {/* White Card Container - matches Figma design */}
      <Box
        ref={containerRef}
        sx={PageStyles.cardContainerStyles}
      >
        {/* Page Title - inside card */}
        <Typography variant="h4" component="h2" sx={PageStyles.pageTitleStyles}>
          Duties required by location
        </Typography>

        {/* Location Selector */}
        <Box sx={PageStyles.locationSelectorContainerStyles}>
          <Typography
            component="label"
            htmlFor="location-select"
            variant="subtitle2"
            sx={PageStyles.locationSelectorLabelStyles}
          >
            Location
          </Typography>
          <FormControl
            fullWidth
            sx={PageStyles.locationSelectorFormControlStyles}
          >
            <Select
              id="location-select"
              value={selectedLocationId}
              onChange={handleLocationChange}
              displayEmpty
              aria-label="Select location"
              inputProps={{
                'aria-describedby': 'location-select-description'
              }}
              sx={PageStyles.locationSelectorSelectStyles}
            >
              {sitesDropDownOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Error Display */}
        {errors.length > 0 && (
          <Box sx={PageStyles.errorBoxContainerStyles}>
            <ErrorBox errors={errors} clearErrors={clearErrors} />
          </Box>
        )}

        {/* Day of Week Tabs */}
        <LocationTabs selectedDay={selectedDay} onDayChange={handleDayChange} />

        {/* Duties Table */}
        <Box sx={PageStyles.tableWrapperStyles}>
          <DutiesTable
            configs={dutiesForDay}
            isLoading={isLoading}
            onConfigure={handleConfigure}
          />
        </Box>
      </Box>

      {/* Configure Duty Modal */}
      <ConfigureDutyModal
        open={isModalOpen}
        config={modalDuty}
        onClose={handleModalClose}
        onSave={handleModalSave}
        isSaving={isSaving}
        container={containerRef.current}
      />
    </Box>
  );
}
